import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { faFileLines } from '@awesome.me/kit-4a6bf60416/icons/classic/regular';
import { toast } from 'sonner';

import { AI_TOAST_MESSAGES } from '@/components/shared/ai-menu-constants';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Icon } from '@/components/ui/icon';
import { Separator } from '@/components/ui/separator';
import { SidebarProvider } from '@/components/ui/sidebar';
import { VisuallyHidden } from '@/components/ui/visually-hidden';
import { useSectionTemplates } from '@/hooks/use-section-templates';
import http, { extractApiErrorMessage } from '@/lib/http';
import { buildSectionsData } from '@/lib/page-utils';
import { cmsPagesEditorIndexPath, cmsPagesEditorPath } from '@/lib/routes';
import { formatErrorMessage, navigateTo, openWindow } from '@/lib/utils';
import { useAiOperationsStore } from '@/stores/ai-operations-store';
import { usePagesActions, usePagesStore } from '@/stores/pages-store';
import { PageType, type SectionStructure } from '@/types/page';
import { AddPageContent } from './add-page-modal/add-page-content';
import { AddPageSidebar } from './add-page-modal/add-page-sidebar';
import {
  BLANK_PAGE_OPTION,
  LAYOUT_HEADERS,
  type SidebarSelection,
} from './add-page-modal/page-template-types';

interface AddPageModalProps {
  trigger?: React.ReactNode;
  childPageType?: string;
  initialSidebarSelection?: string;
  chainedChildPageType?: string;
  openInNewTab?: boolean;
  onSuccess?: () => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onCloseAutoFocus?: (event: Event) => void;
}

export function AddPageModal({
  trigger,
  childPageType,
  initialSidebarSelection,
  chainedChildPageType,
  openInNewTab = false,
  onSuccess,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  onCloseAutoFocus,
}: AddPageModalProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isOpen = controlledOpen ?? uncontrolledOpen;
  const setIsOpen = controlledOnOpenChange ?? setUncontrolledOpen;
  const [isCreating, setIsCreating] = useState(false);
  const isTemplatePicker = !childPageType && !chainedChildPageType;
  const defaultSelection = isTemplatePicker
    ? PageType.PROFILE
    : (initialSidebarSelection ?? BLANK_PAGE_OPTION.key);
  const [selectedItem, setSelectedItem] = useState<SidebarSelection>(defaultSelection);
  const [activeChildPageType, setActiveChildPageType] = useState<string | undefined>(childPageType);
  const layoutPageType = activeChildPageType ?? (chainedChildPageType ? selectedItem : undefined);
  const layoutHeader = LAYOUT_HEADERS.find(({ pageType }) => pageType === layoutPageType);
  const { createPage, loadPages } = usePagesActions();
  const isAiStreaming = useAiOperationsStore((state) => state.isStreaming);
  const { templatesByCode, isLoading: isLoadingSectionTemplates } = useSectionTemplates();

  useEffect(() => {
    if (!isOpen) return;

    setSelectedItem(defaultSelection);
    setActiveChildPageType(childPageType);
    setIsCreating(false);

    // When in chained mode, refresh pages to detect if the index page
    // was already created (e.g. in a previous modal session or another tab).
    // If it exists, skip straight to the child page type templates.
    if (chainedChildPageType && !childPageType && initialSidebarSelection) {
      loadPages().then(() => {
        if (usePagesStore.getState().hasIndexPageType(initialSidebarSelection)) {
          setActiveChildPageType(chainedChildPageType);
        }
      });
    }
  }, [
    isOpen,
    defaultSelection,
    initialSidebarSelection,
    childPageType,
    chainedChildPageType,
    loadPages,
  ]);

  const handleAddTemplate = useCallback(
    async (pageType: string, templateCode?: string, sectionsStructure?: SectionStructure[]) => {
      if (isAiStreaming) {
        toast.error(AI_TOAST_MESSAGES.OPERATION_IN_PROGRESS);
        return;
      }

      if (isLoadingSectionTemplates) {
        toast.error('Section templates are still loading. Please try again in a moment.');
        return;
      }

      setIsCreating(true);

      let sectionsData: SectionStructure[] | undefined;
      try {
        sectionsData = sectionsStructure
          ? await buildSectionsData(sectionsStructure, templatesByCode)
          : undefined;
      } catch (error) {
        toast.error(formatErrorMessage(error, 'Failed to create page'));
        setIsCreating(false);
        return;
      }

      const pageParams = {
        pageType,
        ...(templateCode && { pageTemplateCode: templateCode }),
        ...(sectionsData && { sections: sectionsData }),
      };

      const isChainedIndexCreation = chainedChildPageType && !activeChildPageType;

      if (isChainedIndexCreation) {
        try {
          // Refresh pages to check if the index page was already created
          // (e.g. in another tab), skipping duplicate creation.
          await loadPages();
          const indexAlreadyExists = usePagesStore.getState().hasIndexPageType(pageType);

          if (!indexAlreadyExists) {
            await http.post(cmsPagesEditorIndexPath(), pageParams);
            await loadPages();
          }

          setActiveChildPageType(chainedChildPageType);
        } catch (error) {
          toast.error(extractApiErrorMessage(error) ?? 'Failed to create page');
          setIsOpen(false);
        } finally {
          setIsCreating(false);
        }
      } else if (openInNewTab) {
        const newTab = openWindow('', '_blank');
        try {
          const response = await http.post(cmsPagesEditorIndexPath(), pageParams);
          const data = response.data as { pageId?: string };
          if (data.pageId) {
            const editorPath = cmsPagesEditorPath(data.pageId);
            if (newTab) {
              newTab.location.href = editorPath;
            } else {
              navigateTo(editorPath);
            }
          } else {
            newTab?.close();
          }
          setIsOpen(false);
          onSuccess?.();
        } catch (error) {
          newTab?.close();
          toast.error(extractApiErrorMessage(error) ?? 'Failed to create page');
        } finally {
          setIsCreating(false);
        }
      } else {
        try {
          await createPage(pageParams, {
            onSuccess: () => setIsOpen(false),
            onError: (errors) => {
              toast.error(formatErrorMessage(errors, 'Failed to create page'));
              setIsOpen(false);
            },
          });
        } catch (error) {
          toast.error(formatErrorMessage(error, 'Failed to create page'));
        } finally {
          setIsCreating(false);
        }
      }
    },
    [
      createPage,
      loadPages,
      isAiStreaming,
      chainedChildPageType,
      activeChildPageType,
      templatesByCode,
      isLoadingSectionTemplates,
      onSuccess,
      openInNewTab,
      setIsOpen,
    ],
  );

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      {trigger ? (
        <DialogTrigger asChild>{trigger}</DialogTrigger>
      ) : controlledOpen == null ? (
        <DialogTrigger asChild>
          <Button size="sm">
            <Icon icon={faFileLines} />
            Add Page
          </Button>
        </DialogTrigger>
      ) : null}
      <DialogContent size="4xl" onCloseAutoFocus={onCloseAutoFocus}>
        {layoutHeader ? (
          <>
            <DialogHeader>
              <DialogTitle>{layoutHeader.title}</DialogTitle>
              <DialogDescription>{layoutHeader.description}</DialogDescription>
            </DialogHeader>
            <Separator className="mt-2" />
          </>
        ) : (
          <VisuallyHidden>
            <DialogHeader>
              <DialogTitle>{isTemplatePicker ? 'Select Template' : 'Add Page'}</DialogTitle>
              <DialogDescription>Choose a page template to customize.</DialogDescription>
            </DialogHeader>
          </VisuallyHidden>
        )}
        {activeChildPageType || chainedChildPageType ? (
          <div className="flex flex-1 flex-col w-full gap-4 h-full">
            <div className="flex-1 gap-4 py-4">
              <AddPageContent
                selectedItem={selectedItem}
                templatesByCode={templatesByCode}
                isLoadingSectionTemplates={isLoadingSectionTemplates}
                onAddTemplate={handleAddTemplate}
                isCreating={isCreating}
                childPageType={layoutPageType ?? undefined}
              />
            </div>
          </div>
        ) : (
          <SidebarProvider
            defaultOpen={true}
            className="items-start overflow-y-auto max-h-[calc(100vh-15rem)]"
          >
            <AddPageSidebar selectedItem={selectedItem} onSelectItem={setSelectedItem} />

            <div className="flex flex-1 flex-col w-full gap-4 h-full overflow-y-auto">
              <div className="flex-1 px-2 md:px-6 gap-4 py-4">
                <AddPageContent
                  selectedItem={selectedItem}
                  templatesByCode={templatesByCode}
                  isLoadingSectionTemplates={isLoadingSectionTemplates}
                  onAddTemplate={handleAddTemplate}
                  isCreating={isCreating}
                  childPageType={activeChildPageType}
                />
              </div>
            </div>
          </SidebarProvider>
        )}
      </DialogContent>
    </Dialog>
  );
}
